"use client";

import { useState } from "react";
import { HiOutlineMoon, HiOutlineSun } from "react-icons/hi";

import { Button } from "@/components/ui";

import { ResolvedTheme, useTheme } from "@/contexts/theme-provider";

/**
 * Shared light/dark preview toggle for email surfaces — the reading view
 * (email-body-renderer) and the thread composers (thread-composer). It's split
 * into a headless hook that owns the override cycle and a presentational
 * sun/moon button, because the two surfaces share the control but not how they
 * apply the resolved mode: the reader derives a sandboxed-iframe surface, the
 * composer styles the live editor via CSS. Each also places the button
 * differently, so placement is left to the caller.
 */

/**
 * Owns a per-surface theme override: `null` follows the app theme, otherwise it
 * pins light/dark for this email/draft only. `mode` is the resolved light/dark
 * to render; `toggle` cycles system → opposite → system, the way a per-item
 * override reads (first click flips, second returns to following the app).
 */
export function useEmailThemeToggle() {
  const { resolvedTheme } = useTheme();
  const [override, setOverride] = useState<ResolvedTheme | null>(null);

  // Once the app itself becomes the pinned mode, the pin is redundant — clear
  // it so the surface follows the app for good. Deriving this instead of
  // clearing would only mask the stale value: flipping the app theme away again
  // (e.g. pin dark in a light app → app to dark → app back to light) would
  // resurrect the pin and force a preview the user never re-selected. This is
  // React's "adjust state during render" reset — no effect needed; it settles
  // in one extra render, because the retry sees `override === null`.
  if (override === resolvedTheme) {
    setOverride(null);
  }

  const isOverridden = override !== null;
  const mode: ResolvedTheme = override ?? resolvedTheme;

  // Two states: follow the app, or pin the opposite of the app theme.
  const toggle = () => {
    setOverride(
      isOverridden ? null : resolvedTheme === "dark" ? "light" : "dark",
    );
  };

  return { override, mode, isOverridden, toggle };
}

/**
 * Sun/moon button. `mode` is the current resolved surface mode — a sun offers
 * light while it's dark, a moon offers dark while it's light. Icon-only so it
 * sits among the composer toolbar's formatting buttons and the reader's message
 * actions; the action is conveyed by the tooltip. Placement and the
 * overridden-highlight are the caller's call, passed via `className`.
 */
export function EmailThemeToggle({
  mode,
  onToggle,
  className,
}: {
  mode: ResolvedTheme;
  onToggle: () => void;
  className?: string;
}) {
  const showingDark = mode === "dark";
  const label = showingDark
    ? "Switch email to light mode"
    : "Switch email to dark mode";
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onToggle}
      tooltip={label}
      aria-label={label}
      className={className}
      icon={
        showingDark ? (
          <HiOutlineSun className="py-0" />
        ) : (
          <HiOutlineMoon className="py-0" />
        )
      }
    />
  );
}
